const Plan = {
  ENTERPRISE: 'enterprise',
  BASE: 'base',
  GRID: 'grid',
  CORE: 'core',
  HUB: 'hub',
  PRO: 'pro',
  FREE: 'free'
};

const EnterprisePlans = [Plan.ENTERPRISE, Plan.BASE, Plan.GRID, Plan.CORE, Plan.HUB];
const EntGridPlans = [Plan.ENTERPRISE, Plan.GRID];

export const isSuperOrgPlanEnterprise = (superOrg) => EnterprisePlans.includes(superOrg?.plan?.name);
export const isSuperOrgPlanPro = (superOrg) => superOrg?.plan?.name === Plan.PRO || superOrg?.stripe_subscription_status === 'active';
export const isSuperOrgPlanFree = (superOrg) => superOrg?.plan?.name === Plan.FREE;
export const isSuperOrgPlanBase = (superOrg) => superOrg?.plan?.name === Plan.BASE;
export const isSuperOrgPlanGrid = (superOrg) => EntGridPlans.includes(superOrg?.plan?.name);

export const isUserFree = (user) => {
  const isFree = user?.organizations?.every((org) => isSuperOrgPlanFree(org?.super_organization));

  return isFree;
};

export const getHighestPermissionLevel = (user) => {
  const roleValues = {
    admin: 3,
    write: 2,
    read: 1
  };

  const highestRole = user?.organizations?.reduce((highest, org) => {
    const currentRoleValue = roleValues[org.permission_level] || 0;
    const highestRoleValue = roleValues[highest] || 0;

    return currentRoleValue > highestRoleValue ? org.permission_level : highest;
  }, 'read');

  return highestRole;
};

export function getPermissionLevelInTeam(user, team) {
  const userInTeam = team?.users?.find((teammate) => teammate.id === user?.id);

  return rawTeammateRole(userInTeam);
}

export const rawTeammateRole = (mate) => mate?.permission_level;

export const teammateRole = (teammate) => {
  const permission = teammate?.permission_level;

  switch (permission) {
    case 'admin':
      return 'Admin';
    case 'read':
      return 'Viewer';
    case 'write':
      return 'Creator';
    default:
      return 'No Access';
  }
};

// find user in selectedTeamUsers and update his permission level
export const updatedUserPermissionLevel = (userOrgPermission, selectedTeamUsers) => {
  const user = userOrgPermission.user || userOrgPermission;
  const permissionLevel = userOrgPermission.permission_level;
  const updatedTeammates = selectedTeamUsers.map((mate) => {
    const updatedMate = { ...mate };

    if (updatedMate.id === user.id) {
      return { ...updatedMate, permission_level: permissionLevel };
    }
    return updatedMate;
  });

  return updatedTeammates;
};

export const canSharePublicly = (activeTeam) => {
  if (!activeTeam) {
    return false;
  }
  return (
    !isSuperOrgPlanEnterprise(activeTeam?.super_organization) ||
    (isSuperOrgPlanEnterprise(activeTeam?.super_organization) &&
      ((activeTeam?.override_superorg_permissions && activeTeam.maximum_world_permission != null) ||
        (!activeTeam?.override_superorg_permissions && activeTeam?.super_organization.maximum_world_permission != null)))
  );
};

export const SuperOrganizationRoleCodes = {
  SystemAdmin: 300,
  TeamCreator: 200,
  ScribeCreator: 100
};

export const allPermissions = [
  'add_organization',
  'change_organization',
  'delete_organization',
  'view_organization',
  'view_scribeuser',
  'can_super_admin',
  'change_superorganization',
  'view_superorganization',
  'add_superorganizationrole',
  'change_superorganizationrole',
  'delete_superorganizationrole',
  'view_superorganizationrole',
  'add_userorganizationpermission',
  'change_userorganizationpermission',
  'delete_userorganizationpermission',
  'view_userorganizationpermission'
];

const permissionCodenameToNameMap = {
  add_organization: 'Can add team',
  change_organization: 'Can edit team',
  delete_organization: 'Can delete team',
  view_organization: 'Can view team',
  view_scribeuser: 'Can view users',
  can_super_admin: 'Has organization admin permission',
  change_superorganization: 'Can edit organization',
  view_superorganization: 'Can view organization',
  add_superorganizationrole: 'Can add role',
  change_superorganizationrole: 'Can edit role',
  delete_superorganizationrole: 'Can delete role',
  view_superorganizationrole: 'Can view role',
  add_userorganizationpermission: 'Can add user team permission',
  change_userorganizationpermission: 'Can edit user team permission',
  delete_userorganizationpermission: 'Can delete user team permission',
  view_userorganizationpermission: 'Can view user team permission'
};

const permissionCodenameToRequiredRoleMap = {
  add_organization: SuperOrganizationRoleCodes.TeamCreator,
  change_organization: SuperOrganizationRoleCodes.TeamCreator,
  delete_organization: SuperOrganizationRoleCodes.TeamCreator,
  view_organization: SuperOrganizationRoleCodes.ScribeCreator,
  view_scribeuser: SuperOrganizationRoleCodes.ScribeCreator,
  can_super_admin: SuperOrganizationRoleCodes.SystemAdmin,
  change_superorganization: SuperOrganizationRoleCodes.SystemAdmin,
  view_superorganization: SuperOrganizationRoleCodes.ScribeCreator,
  add_superorganizationrole: SuperOrganizationRoleCodes.SystemAdmin,
  change_superorganizationrole: SuperOrganizationRoleCodes.SystemAdmin,
  delete_superorganizationrole: SuperOrganizationRoleCodes.SystemAdmin,
  view_superorganizationrole: SuperOrganizationRoleCodes.ScribeCreator,
  add_userorganizationpermission: SuperOrganizationRoleCodes.ScribeCreator,
  change_userorganizationpermission: SuperOrganizationRoleCodes.SystemAdmin,
  delete_userorganizationpermission: SuperOrganizationRoleCodes.SystemAdmin,
  view_userorganizationpermission: SuperOrganizationRoleCodes.TeamCreator
};

export const mapPermissionToName = (permission) => permissionCodenameToNameMap[permission.codename] || permission.name;

export const getMappedPermissions = (code) => {
  if (!code) {
    code = 0;
  }
  return allPermissions.map((permission) => ({
    codename: permission,
    name: permissionCodenameToNameMap[permission],
    enabled: code >= permissionCodenameToRequiredRoleMap[permission]
  }));
};

export const ROLE_EXPLANATIONS = {
  admin:
    'Admins have the ability to modify team-wide settings, invite users, remove users, and change user settings. They can also create, update, view, and delete Scribes. On some plans, they can also mark scribes as verified or under review and have access to the review queue.',
  review:
    'Reviewers can mark scribes as "verified" or "under review" and have access to the review queue. Like creators, they can also create, update, view, and delete Scribes.',
  write:
    'Creators can create, update, view, and delete Scribes. They cannot modify team wide settings, review Scribes, or remove users. They can invite users.',
  read: 'Viewers can search for and view Scribes but they cannot create, update, or delete them.'
};

export const ADDITIONAL_VIEWER_EXPLANATION =
  "If you change this user's role to Viewer, the documents they own will be transferred to a team admin.";
